import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, Params, Route, Router} from '@angular/router';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {filter, first, map, takeUntil} from 'rxjs/operators';
import {BreadcrumbObj, BreadcrumbType, MxTitleAndBreadcrumbsService} from 'mx-core';
import {MxRecentlyUsedService} from 'mx-core';

const BREADCRUMB_CONTROLLER: string = '/rest/breadcrumbs';



export class BreadCrumb {
    title?: string;
    type?: string;
    id?: string;
    link?: string;
    icon?: string;
    tooltip?: string;
    queryParam?: Params;
}

@Injectable({
    providedIn: 'root'
})
export class PrlTitleAndBreadcrumbsService implements MxTitleAndBreadcrumbsService {

    private _destroy$$: Subject<null> = new Subject<null>();
    private _breadCrumbs: BehaviorSubject<Array<BreadCrumb>> = new BehaviorSubject<Array<BreadCrumb>>([]);
    private _titles: Subject<string> = new Subject<string>();
    private appTitle: string;


    constructor(
        private router: Router,
        private titleService: Title,
        private route: ActivatedRoute,
        public recentlyUsedService: MxRecentlyUsedService,
        private httpClient: HttpClient
    ) {
        this.appTitle =  this.titleService.getTitle();
    }

    public setTitle(title: string): void {
        this._titles.next(title);
    }

    public getBreadcrumbObsr(): Observable<Array<BreadCrumb>> {
        return this._breadCrumbs;
    }

    private initRouteTracking(): void {
        this.router
            .events.pipe(
            takeUntil(this._destroy$$),
            filter(event => event instanceof NavigationEnd),
            map(() => this.route.firstChild),
            filter((route) => route.outlet === 'primary'),
        ).subscribe(route => this.evalTitleAndBreadcrumbs(route));
    }

    private evalTitleAndBreadcrumbs(route: ActivatedRoute): any {
        let pathArray = route.routeConfig.path.split('/');
        let queryParam: Params = route.snapshot.queryParams;
        let breadcrumbsConfig: Array<BreadCrumb> = [];
        let breadcrumbSkip: Array<String> = route.snapshot.data['breadcrumbSkip'] || [];
        let breadcrumbEnable: boolean = !!route.snapshot.data['breadcrumbEnable'];
        let currLink = [];
        for (let i = 0; i < pathArray.length; i++) {
            if (pathArray[i] && !breadcrumbSkip.includes(pathArray[i]) && pathArray[i].indexOf(':') > -1) {
                let track = pathArray.slice(0, i).join('/');
                let regexStr = track + '/:\\w+$';
                let regex = new RegExp(regexStr);
                let partRoute = this.getRouteByPattern(regex);
                let type, icon, title, routeBreadcrumbSkip;
                if (partRoute.data) {
                    type = partRoute.data['breadcrumbType'];
                    icon = partRoute.data['breadcrumbIcon'] || 'label_important';
                    title = partRoute.data['breadcrumbTitle'];
                    routeBreadcrumbSkip =  partRoute.data['breadcrumbSkip'] || [];
                }

                let id = route.snapshot.url[i].path;
                currLink.push(id);
                let link = currLink.join('/');
                if (pathArray.length > i + 1 && !breadcrumbSkip.includes(pathArray[i + 1])
                    && !routeBreadcrumbSkip.includes(pathArray[i + 1])) {
                    link = `${link}/${pathArray[i + 1]}`;
                }
                let breadConf: BreadCrumb = {title, type, id, link, icon, queryParam};
                breadcrumbsConfig.push(breadConf);
            } else if (!breadcrumbSkip.includes(pathArray[i])) {
                currLink.push(pathArray[i]);
            }
        }
        if ((this._breadCrumbs.value.length > 0 && breadcrumbsConfig.length > 0
            && this._breadCrumbs.value[0].type !== breadcrumbsConfig[0].type)
            || (this._breadCrumbs.value.length > 0 && breadcrumbsConfig.length === 0)) {
            this._breadCrumbs.next([]);
        }
        if (breadcrumbsConfig.length === 0 || !breadcrumbEnable) {
            if (Object.entries(route.snapshot.params).length > 0) {
                this._titles.pipe(first())
                    .subscribe(value => this.titleService.setTitle(this.appTitle + ' → ' + this.titleCase(value, true)));
            } else if (route.snapshot.data['formName']) {
                this.titleService.setTitle(this.appTitle + ' → ' + route.snapshot.data['formName']);
            } else if (pathArray.length === 1) {
                this.titleService.setTitle(this.appTitle + ' → ' + this.titleCase(pathArray[0]).replace(/-/g, ' '));
            }
        } else {
            this.getBreadcrumbsFromServer(breadcrumbsConfig.map(value => {
                return {type: value.type as BreadcrumbType, id: value.id};
            }))
                .subscribe(value => {
                    for (let i = 0; i < breadcrumbsConfig.length; i++) {
                        breadcrumbsConfig[i].title ? breadcrumbsConfig[i].title += value[i] : breadcrumbsConfig[i].title = value[i];
                    }
                    this._breadCrumbs.next(breadcrumbsConfig);
                    this.recentlyUsedService.addToRecentlyOpened(breadcrumbsConfig);
                    if (breadcrumbsConfig.length > 0) {
                        let title = breadcrumbsConfig.map(item => this.titleCase(item.type).replace(/_/g, ' ') + ' ' + item.title).join(' → ');
                        this.titleService.setTitle(this.appTitle + ' → ' + title);
                    }
                });
        }
    }

    private titleCase(str: string, onlyFirstLetter: boolean = false): string {
        return (str && str.substr(0, 1).toUpperCase() + (onlyFirstLetter ? str.substr(1) : str.substr(1).toLowerCase())) || '';
    }

    private getRouteByPattern(pattern: RegExp): Route {
        return this.router.config.find(value => value.path.match(pattern) !== null);
    }

    private getBreadcrumbsFromServer(config: Array<BreadcrumbObj>): Observable<Array<string>> {
        return this.httpClient.post<Array<string>>(BREADCRUMB_CONTROLLER, config);
    }

    init(): Observable<void> {
        this.initRouteTracking();
        return of(void 0);
    }

    destroy(): void {
        this._destroy$$.next(null);
    }

}
